import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {announce} from '@github-ui/aria-live'
import {
  fetchMarkdownEditHistoryMetadata,
  markdownEditHistoryMetadataQueryKey,
} from '@github-ui/markdown-edit-history-viewer/fetch-markdown-edit-history-metadata'
import {skipToken, useQuery} from '@github-ui/react-query'
import {Button} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'
import {useEffect, useRef, type RefObject} from 'react'

import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {useLayoutPullRequest} from '../../stores/layout-selectors'
import styles from './Description.module.css'

export function DescriptionEditHistory({returnFocusRef}: {returnFocusRef: RefObject<HTMLElement | null>}) {
  const {relayId} = useLayoutPullRequest()
  const {data: descriptionData} = useDescriptionData()
  const editInformation = descriptionData?.editInformation
  const hasKnownHistory = Boolean(editInformation?.viewerCanReadUserContentEdits && editInformation.lastUserContentEdit)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const hasFocusRef = useRef(false)
  // React focus events include the portaled menu and dialog; DOM containment does not.
  const focusProps = {
    onFocusCapture: () => {
      hasFocusRef.current = true
    },
    onBlurCapture: () => {
      hasFocusRef.current = false
    },
  }
  const {data, isError, isPending, isFetching, refetch} = useQuery({
    queryKey: markdownEditHistoryMetadataQueryKey(relayId ?? ''),
    queryFn: relayId ? ({signal}) => fetchMarkdownEditHistoryMetadata(relayId, signal) : skipToken,
    // Seed known absence instead of disabling: saves and Alive must still refetch it.
    initialData: editInformation && !hasKnownHistory ? null : undefined,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (hasFocusRef.current && document.activeElement === document.body) {
      const target = anchorRef.current ?? returnFocusRef.current
      hasFocusRef.current = false
      target?.focus()
    }
  })

  useEffect(() => {
    if (isError) announce('Description history is unavailable. Retry to load it again.')
  }, [isError])

  if (!relayId) return null

  if (!isPending && !isError && (!data?.viewerCanReadUserContentEdits || !data.lastEditedAt)) return null

  return (
    <div {...focusProps}>
      {isPending || isError ? (
        <Button
          ref={anchorRef}
          variant="invisible"
          size="small"
          trailingAction={!isError && hasKnownHistory ? TriangleDownIcon : undefined}
          className={isError ? undefined : styles.editHistoryLoading}
          loading={isPending || isFetching}
          onClick={() => void refetch()}
        >
          {isError ? (
            'History unavailable. Retry'
          ) : hasKnownHistory ? (
            <>
              <span className={styles.editHistoryLabel}>Edited</span>
              <span className="sr-only"> (loading description edit history)</span>
            </>
          ) : (
            'Loading description history'
          )}
        </Button>
      ) : (
        <MarkdownEditHistoryViewer
          editHistory={data ?? undefined}
          renderAnchor={() => (
            <>
              <span className={styles.editHistoryLabel}>Edited</span>
              <span className="sr-only"> (view description edit history)</span>
            </>
          )}
          anchorRef={anchorRef}
        />
      )}
    </div>
  )
}
